<script setup lang="ts">
import type { ISelectItem } from "@/types"
import { useQueryClient } from "@tanstack/vue-query"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { computed, ref, watch } from "vue"
import z from "zod"
import { GET_EVENT_VAULT_QUERY_KEY, useCreateWebsite, useGetEventVault, useListDomains, useUpdateWebsite } from "@/api"
import { Button, Form, FormSection, Input, Select, Switch } from "@/components/ui"
import { useTranslation } from "@/composables"
import { ValidationException } from "@/http"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { toSlug } from "@/utils"
import { useToast } from "../ui/ToastReka/useToast"

interface WebsiteFormProps {}

defineProps<WebsiteFormProps>()

const { t } = useTranslation()
const toast = useToast()
const eventStore = useEventVaultStore()
const eventVaultId = computed(() => eventStore.eventVaultId)
const { data: eventVault } = useGetEventVault(eventVaultId, { include: ["website"] })
const eventType = computed(() => eventVault.value?.eventType)
const domainsEnabled = computed(() => Boolean(eventType.value))
const { data: domains } = useListDomains(eventType, domainsEnabled)
const queryClient = useQueryClient()
const { mutate: createWebsite, isPending: isPendingCreate } = useCreateWebsite()
const { mutate: updateWebsite, isPending: isPendingUpdate } = useUpdateWebsite()

const validationSchema = toTypedSchema(z.object({
  domainKey: z.string().min(1, t("website_form.validation.domain_required")),
  slug: z.string()
    .transform(value => toSlug(value))
    .refine(value => value.length > 0, t("website_form.validation.slug_required"))
    .refine(value => value.length >= 3, t("website_form.validation.slug_min_length"))
    .refine(value => value.length <= 60, t("website_form.validation.slug_max_length"))
    .refine(value => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value), t("website_form.validation.slug_invalid")),
  isPublic: z.boolean(),
}))

const { handleSubmit, setFieldValue, values } = useForm({
  validationSchema,
  initialValues: {
    domainKey: "",
    slug: "",
    isPublic: false,
  },
})

const validationErrors = ref<Record<string, string[]>>()

function handleValidationError(error: unknown) {
  if (error instanceof ValidationException) {
    validationErrors.value = error.validationErrors
    return
  }

  toast.error()
}

// Pre-fills the form from the existing website exactly once. `immediate: true`
// is required because vue-query may already have this data cached (e.g. on a
// revisit within the query's gcTime) — in that case `eventVault.value?.website`
// is already populated at setup and a non-immediate watcher would never see it
// "change", leaving the form blank. The `hasPrefilled` flag still ensures we
// only populate once, so a later refetch (e.g. after save invalidates the
// query) doesn't clobber in-progress edits.
let hasPrefilled = false
watch(() => eventVault.value?.website, (website) => {
  if (hasPrefilled || !website)
    return

  hasPrefilled = true
  setFieldValue("domainKey", website.domainKey)
  setFieldValue("slug", website.slug)
  setFieldValue("isPublic", website.isPublic)
}, { immediate: true })

function handleSlugBlur() {
  setFieldValue("slug", toSlug(values.slug ?? ""))
}

function handleGenerateSlug() {
  if (values.slug?.trim())
    return

  const title = eventVault.value?.title
  if (!title)
    return

  setFieldValue("slug", toSlug(title))
}

const onSubmit = handleSubmit((values) => {
  if (!eventStore.eventVaultId) {
    toast.error()
    return
  }

  if (eventVault.value?.website) {
    updateWebsite({ eventVaultId: eventStore.eventVaultId, ...values }, {
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_VAULT_QUERY_KEY] })
        toast.success()
      },
      onError(error) {
        handleValidationError(error)
      },
    })
  }
  else {
    createWebsite({ eventVaultId: eventStore.eventVaultId, ...values }, {
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_VAULT_QUERY_KEY] })
        toast.success()
      },
      onError(error) {
        handleValidationError(error)
      },
    })
  }
})

const availableDomains = computed<ISelectItem[]>(() => {
  return domains.value?.map(domain => ({ label: domain, value: domain })) ?? []
})
</script>

<template>
  <Form @submit="onSubmit">
    <FormSection variant="divider" icon="Globe" :title="t('website_form.section_title')" :description="t('website_form.section_description')">
      <div class="flex flex-col gap-4">
        <Select
          name="domainKey"
          :items="availableDomains"
          :label="t('website_form.domain_label')"
          :hint="t('website_form.domain_hint')"
          :error="validationErrors?.DomainKey?.[0] ?? validationErrors?.Domain?.[0] ?? validationErrors?.Unavailable?.[0]"
        />
        <div>
          <Input
            name="slug"
            :label="t('website_form.slug_label')"
            :hint="t('website_form.slug_hint')"
            :error="validationErrors?.Length?.[0] ?? validationErrors?.Reserved?.[0] ?? validationErrors?.Profanity?.[0] ?? validationErrors?.Error?.[0] ?? validationErrors?.Empty?.[0]"
            clearable
            @blur="handleSlugBlur"
          />
          <div class="mt-2">
            <Button
              type="button"
              size="sm"
              :disabled="Boolean(values.slug?.trim()) || !eventVault?.title"
              @click="handleGenerateSlug"
            >
              {{ t("website_form.generate") }}
            </Button>
          </div>
        </div>

        <div>
          <div class="flex flex-col gap-2 md:flex-row items-start md:gap-14 mt-3">
            <Switch
              name="isPublic"
              variant="icon"
              :label="t('website_form.is_public_label')"
            />
            <p class="text-stone-500 text-sm">
              {{ t("website_form.privacy_description") }}
            </p>
          </div>
        </div>
      </div>
    </FormSection>

    <template #actions>
      <div class="flex justify-end">
        <Button type="submit" :disabled="isPendingCreate || isPendingUpdate" :suffix-icon="isPendingCreate || isPendingUpdate ? 'Loading' : undefined">
          {{ t('submit') }}
        </Button>
      </div>
    </template>
  </Form>
</template>
